import {
  AlertDisplayType,
  type LanguageObject,
  type LaunchDarklyAlert,
} from './types';

export interface AlertAudienceContext {
  isLeaderOrOperator: boolean;
  hasComplianceEnabled: boolean;
  hasXpCompliancePlans: boolean;
  isInCompliancePhaseSegment: boolean;
  isInChainWideRollout: boolean;
}

export interface AlertsToShow {
  banners: LaunchDarklyAlert[];
  messageBlocks: LaunchDarklyAlert[];
}

/** userId → ids of message blocks that user dismissed on this device. */
export type AlertDismissals = Record<string, string[]>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toLanguageObject = (value: unknown): LanguageObject | undefined => {
  if (!isRecord(value) || typeof value.en !== 'string') return undefined;
  return typeof value.es === 'string'
    ? { en: value.en, es: value.es }
    : { en: value.en };
};

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

const isAlertDisplayType = (value: unknown): value is AlertDisplayType =>
  value === AlertDisplayType.Banner || value === AlertDisplayType.MessageBlock;

/**
 * Validates required fields (dropping the entry if any is bad) and strips
 * malformed optional fields so renderers only ever see well-typed data.
 */
const toAlert = (value: unknown): LaunchDarklyAlert | undefined => {
  if (!isRecord(value)) return undefined;
  const { startDate, endDate, status } = value;
  const message = toLanguageObject(value.message);
  if (
    typeof startDate !== 'number' ||
    typeof endDate !== 'number' ||
    typeof status !== 'boolean' ||
    !message
  ) {
    return undefined;
  }

  const alert: LaunchDarklyAlert = { startDate, endDate, message, status };

  const title = toLanguageObject(value.title);
  if (title) alert.title = title;

  if (isRecord(value.link) && isNonEmptyString(value.link.url)) {
    const text = toLanguageObject(value.link.text);
    if (text) alert.link = { text, url: value.link.url };
  }

  if (typeof value.alertType === 'string') alert.alertType = value.alertType;
  if (isAlertDisplayType(value.displayType)) {
    alert.displayType = value.displayType;
  }

  alert.id = isNonEmptyString(value.id)
    ? value.id
    : `${String(startDate)}|${String(endDate)}|${JSON.stringify(message)}|${alert.link?.url ?? ''}`;

  return alert;
};

/**
 * Turns the raw `web-alert-banner` flag value into a list of alerts, each with
 * an `id`. Accepts a single alert, an array, or nothing; drops the OFF
 * variation (`{ message: null, status: false }`) and entries with bad required
 * fields, and removes malformed optional fields from the rest.
 */
export const normalizeAlerts = (flag: unknown): LaunchDarklyAlert[] => {
  const list: unknown[] = Array.isArray(flag) ? flag : [flag];
  return list.flatMap(entry => toAlert(entry) ?? []);
};

export const getDisplayType = (alert: LaunchDarklyAlert): AlertDisplayType =>
  alert.displayType === AlertDisplayType.MessageBlock
    ? AlertDisplayType.MessageBlock
    : AlertDisplayType.Banner;

const matchesAudience = (
  alert: LaunchDarklyAlert,
  ctx: AlertAudienceContext,
): boolean => {
  if (alert.alertType === 'CompliancePlanCompletionNoticePhased') {
    return (
      ctx.hasComplianceEnabled &&
      !ctx.hasXpCompliancePlans &&
      ctx.isInCompliancePhaseSegment
    );
  }

  if (alert.alertType === 'CompliancePlanCompletionNoticeChainWide') {
    return (
      ctx.hasComplianceEnabled &&
      !ctx.hasXpCompliancePlans &&
      ctx.isInChainWideRollout
    );
  }

  return ctx.isLeaderOrOperator;
};

/**
 * Eligible alerts (has id, status on, inside the epoch-seconds date window,
 * audience matches) split by display type. Does not apply dismissals; run
 * `messageBlocks` through `filterUndismissed`.
 */
export const computeAlertsToShow = (
  flag: unknown,
  ctx: AlertAudienceContext,
  now: number = Math.floor(Date.now() / 1000),
): AlertsToShow => {
  const eligible = normalizeAlerts(flag).filter(
    alert =>
      Boolean(alert.id) &&
      alert.status &&
      now >= alert.startDate &&
      now <= alert.endDate &&
      matchesAudience(alert, ctx),
  );

  return {
    banners: eligible.filter(
      alert => getDisplayType(alert) === AlertDisplayType.Banner,
    ),
    messageBlocks: eligible.filter(
      alert => getDisplayType(alert) === AlertDisplayType.MessageBlock,
    ),
  };
};

/**
 * Message blocks the user has not dismissed. Without a user id a dismissal
 * can't be recorded, so nothing is shown.
 */
export const filterUndismissed = (
  messageBlocks: LaunchDarklyAlert[],
  dismissals: AlertDismissals,
  userId: string,
): LaunchDarklyAlert[] => {
  if (!userId) return [];
  const dismissed = dismissals[userId] ?? [];
  return messageBlocks.filter(
    alert => alert.id !== undefined && !dismissed.includes(alert.id),
  );
};

/** Returns a new dismissals map with `id` recorded for `userId` (deduped). */
export const addDismissal = (
  dismissals: AlertDismissals,
  userId: string,
  id: string,
): AlertDismissals => {
  const existing = dismissals[userId] ?? [];
  if (existing.includes(id)) return dismissals;
  return { ...dismissals, [userId]: [...existing, id] };
};

const isLanguageKey = (language: string): language is keyof LanguageObject =>
  language === 'en' || language === 'es';

/** Text in `language`, falling back to English. */
export const getLocalizedText = (
  obj: LanguageObject,
  language: string,
): string => {
  const text: unknown = isLanguageKey(language) ? obj[language] : undefined;
  return typeof text === 'string' ? text : obj.en;
};
